import {Injectable} from '@angular/core';
import {HttpClient, HttpStatusCode} from "@angular/common/http";
import {BehaviorSubject, catchError, map, Observable, of, tap} from "rxjs";
import CryptoES from 'crypto-es';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    // @ts-ignore
    private _user: BehaviorSubject<User | null> = new BehaviorSubject(null)

    get user(): Observable<User | null> {
        return this._user;
    }

    constructor(private httpClient: HttpClient) {
        this.httpClient.get<User>("/rest/auth/get_user")
            .pipe(catchError(() => of(null)))
            .subscribe(value => this._user.next(value))
    }

    requestPasswordReset(email: string): Observable<RequestPasswordResetResult> {
        const data = {
            id: email
        };
        return this.httpClient.post("/rest/auth/request_reset", data).pipe(
            map(value => RequestPasswordResetResult.Requested),
            catchError((error) => of(RequestPasswordResetResult.Error))
        )
    }

    signInWithEmail(email: string, password: string): Observable<SignInResult> {
        const data = {
            id: email,
            token: this.getToken(email, password)
        };
        const service = this;
        return this.httpClient.post<User>("/rest/auth/login", data).pipe(
            tap(value => service._user.next(value)),
            map(value => SignInResult.Success),
            catchError((error) => of(SignInResult.Error))
        )
    }

    signUpWithEmail(name: string, email: string, password: string): Observable<SignUpResult> {
        const data = {
            id: email,
            name: name,
            token: this.getToken(email, password)
        };
        const service = this;
        return this.httpClient.post<User>("/rest/auth/signup", data).pipe(
            tap(value => service._user.next(value)),
            map(value => SignUpResult.Success),
            catchError((error) => of(SignUpResult.Error))
        )
    }

    resetPassword(email: string, password: string, token: string): Observable<PasswordResetResult> {
        const data = {
            id: email,
            code: token,
            token: this.getToken(email, password)
        };
        return this.httpClient.post<User>("/rest/auth/reset", data).pipe(
            map(value => PasswordResetResult.Success),
            catchError((error) => {
                let result = PasswordResetResult.Error;
                if (error.code == HttpStatusCode.BadRequest) {
                    result = PasswordResetResult.TokenExpired
                }
                return of(result)
            })
        )
    }

    private getToken(email: string, password: string): string {
        return CryptoES.SHA256(email.toLowerCase() + password)
            .toString(CryptoES.enc.Hex)
            .toUpperCase()
    }

}

export enum RequestPasswordResetResult {
    Requested, Error
}

export enum PasswordResetResult {
    Success, TokenExpired, Error
}

export enum SignInResult {
    Success, Error
}

export enum SignUpResult {
    Success, Error
}

export interface User {
    name: string
    email: string
}